export const venueCategories = [
  "food",
  "groceries",
  "retail",
  "services",
  "entertainment",
] as const;

export type VenueCategory = (typeof venueCategories)[number];
export type HoursStatus = "parsed" | "conditional" | "missing" | "invalid";
export type VenueState = "open" | "closed" | "uncertain" | "missing" | "invalid";

export interface Bounds {
  west: number;
  south: number;
  east: number;
  north: number;
}

export interface CityConfig {
  id: string;
  displayName: string;
  country: string;
  timezone: string;
  locale: string;
  sourceExtractUrl: string;
  openingHoursAddress: {
    countryCode: string;
    state: string;
  };
  bounds: Bounds;
  center: [number, number];
  defaultZoom: number;
  mapStyleUrl: string;
  dataNotes?: string;
}

export interface ScheduleMask {
  status: HoursStatus;
  source?: "osm" | "official_website";
  effectiveExpression?: string;
  sourceField?: "opening_hours" | "opening_hours:kitchen";
  openMask?: string;
  uncertainMask?: string;
}

export interface Venue {
  id: string;
  name: string;
  coordinates: [number, number];
  category: VenueCategory;
  subtype: string;
  schedule: ScheduleMask;
  originalHours?: string;
  kitchenHours?: string;
  website?: string;
  websiteHours?: {
    expression: string;
    sourceUrl: string;
    extractedAt: string;
    extractionMethod: "schema.org_json_ld";
  };
  address?: string;
  brand?: string;
  h3: string;
  osmUrl: string;
  provenance: "OpenStreetMap";
}

export interface CategoryQuality {
  total: number;
  withHours: number;
  parsed: number;
}

export interface DataQuality {
  relevantVenueCount: number;
  withHoursCount: number;
  parsedScheduleCount: number;
  missingScheduleCount: number;
  invalidScheduleCount: number;
  conditionalScheduleCount: number;
  websiteHoursBackfillCount?: number;
  websiteHoursEligibleCount?: number;
  duplicateAuditCount: number;
  byCategory: Record<VenueCategory, CategoryQuality>;
  sourceDataTimestamp?: string;
  generatedDataTimestamp: string;
}

export interface CityManifest {
  schemaVersion: 1 | 2;
  city: Pick<
    CityConfig,
    | "id"
    | "displayName"
    | "country"
    | "timezone"
    | "locale"
    | "bounds"
    | "center"
    | "defaultZoom"
    | "mapStyleUrl"
    | "dataNotes"
  >;
  window: {
    start: string;
    end: string;
    timezone: string;
    intervalMinutes: 15;
    slots: number;
  };
  assets: { venues: string | string[] };
  quality: DataQuality;
  generatedAt: string;
}

export interface CityIndexEntry {
  id: string;
  displayName: string;
  center: [number, number];
  manifest: string;
}

export interface CellSummary {
  h3: string;
  open: number;
  possible: number;
  total: number;
  known: number;
  missingHours: number;
  categories: Record<VenueCategory, number>;
  openVenues: Venue[];
}
